/**
 * One automation on a phone and an iPad, in the Season design: its steps in order with their counts, the switch, and
 * what is happening in it. A tap on a step changes its words, its wait or its check; building steps is for a computer.
 * On the iPad on its side this is the two panes beside the list: the steps, and the open step or the summaries.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { ActionsSheet } from "../components/season/ActionsSheet";
import { DockedBar } from "../components/season/DockedBar";
import { Empty } from "../components/season/Empty";
import { GroupedList, ListRow } from "../components/season/GroupedList";
import type { SeasonLayout } from "../components/season/layout";
import { Page } from "../components/season/Page";
import { Pane } from "../components/season/Panes";
import { PushedNav } from "../components/season/PushedNav";
import { Button } from "../components/ui/Button";
import { Icon } from "../components/ui/Icon";
import { Pill } from "../components/ui/Pill";
import { Sheet } from "../components/ui/Sheet";
import { Toggle } from "../components/ui/Toggle";
import { useToast } from "../components/ui/Toast";
import { type Editor } from "../components/FlowEditor";
import { unfinished } from "./flow-model";
import { AttentionCard, attentionText, HowCard, NowCard, RunsCard, StepsLine } from "./seasonParts";
import { AttentionSheet, DeleteSheet, RenameSheet } from "./seasonSheets";
import { targetTitle, useStepEditor } from "./SeasonStepForm";
import { useAutomation } from "./useAutomation";
import { errorText, fmt, people, waitingRoom } from "./words";
import "../styles/season-autos.css";

/** The open step's form: a sheet on a phone and an iPad upright. */
function StepSheet({ editor, target, save, close }: { editor: Editor; target: string; save: (next: Editor) => Promise<void>; close: () => void }) {
  const form = useStepEditor(editor, target, save, close);
  return (
    <Sheet open onClose={close} title={targetTitle(editor, target)} footer={
        <>
          {form.cancel}
          {form.save}
        </>
      }
    >
      {form.fields}
    </Sheet>
  );
}

/** The open step's form in the right pane of the iPad on its side. */
function StepPane({ editor, target, save, close }: { editor: Editor; target: string; save: (next: Editor) => Promise<void>; close: () => void }) {
  const form = useStepEditor(editor, target, save, close);
  return (
    <div className="au-sp">
      <div className="au-sph">
        <h2>{targetTitle(editor, target)}</h2>
        <button type="button" className="k-ib" aria-label="Close" onClick={close}>
          <Icon name="x" />
        </button>
      </div>
      {form.fields}
      <div className="au-btns">
        {form.save}
        {form.cancel}
      </div>
    </div>
  );
}

export function SeasonAutomation({
  id,
  layout,
  embedded = false,
  tick = 0,
  onChanged,
}: {
  id: string;
  layout: SeasonLayout;
  embedded?: boolean;
  /** Counts up when the list changed this automation's switch, so it reads it again. */
  tick?: number;
  /** Called after this automation changed or went, so the list beside it reads again. */
  onChanged?: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const A = useAutomation(id);
  const { detail: d, editor, who, missing, reload } = A;
  const [target, setTarget] = useState<string | null>(null);
  const [menu, setMenu] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [attn, setAttn] = useState(false);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const side = layout === "side";
  const phone = layout === "phone";

  const seen = useRef(tick);
  useEffect(() => {
    if (seen.current === tick) return;
    seen.current = tick;
    reload();
  }, [tick, reload]);

  const nav = (right?: React.ReactNode) => (embedded ? null : <PushedNav back={{ to: "/automations", label: "Automations" }} right={right} />);

  if (missing || !d || !editor) {
    const body = (
      <Page layout={layout} className="au">
        {nav()}
        {missing ? (
          <Empty title={missing}>
            <Button onClick={() => navigate("/automations")}>All automations</Button>
          </Empty>
        ) : (
          <span aria-busy="true" />
        )}
      </Page>
    );
    return embedded ? (
      <>
        <Pane kind="main" label="The automation that is open">
          {body}
        </Pane>
        <Pane kind="detail" label="Details">{null}</Pane>
      </>
    ) : (
      body
    );
  }

  const status = d.status;
  const draft = status === "draft";
  const on = status === "on";
  const attached = d.webinars.filter((w) => w.attached).map((w) => ({ ...w, enabled: w.enabled === 1 }));
  const waiting = waitingRoom(status, attached);
  const inNow = who ? Object.values(who.at).reduce((n, l) => n + l.length, 0) : 0;
  const whenSub = draft ? "Not running yet" : d.enroll ? `${fmt(d.enroll.entered)} joined so far` : "";
  const failed = attentionText(d);

  const changed = (p: Promise<unknown>) => void p.then(() => onChanged?.());
  const setStatus = (next: "on" | "off") => changed(A.setStatus(d, next));
  const settle = (how: "retry" | "stop") => {
    setBusy(true);
    A.settleRuns(d, how)
      .catch((err) => toast(errorText(err, "That did not work just now")))
      .finally(() => setBusy(false));
  };
  const save = async (next: Editor) => {
    try {
      await A.saveFlow(next, status);
      setTarget(null);
      toast("Steps saved.");
    } catch (err) {
      toast(errorText(err, "The steps did not save"));
    }
  };
  // Why off, not on: the computer's Finish it ends a draft, which is then switched on by hand.
  const finish = async () => {
    const unready = unfinished(editor.trigger, editor.steps);
    if (unready) {
      toast({ text: unready.words, icon: "info" });
      setTarget(unready.uid);
      return;
    }
    try {
      await A.saveFlow(editor, editor.steps.length ? "off" : status);
      toast("Steps saved.");
      onChanged?.();
    } catch (err) {
      toast(errorText(err, "The steps did not save"));
    }
  };

  const menuButton = (
    <button type="button" className="k-ib au-more-b" aria-label="More" onClick={() => setMenu(true)}>
      <Icon name="more" />
    </button>
  );
  const hero = (
    <div className="au-hero">
      <h1 className="k-h1">{d.name}</h1>
      {draft ? <Pill tone="warn">Draft</Pill> : null}
      {side ? menuButton : null}
    </div>
  );
  const statusSwitch = draft ? (
    <Button variant="p" className="au-fin" onClick={() => void finish()}>
      Finish it
    </Button>
  ) : (
    <div className="k-gl au-sw">
      <div className="k-set">
        <span className="k-set-t">
          <b>{on ? "On" : "Off"}</b>
        </span>
        <Toggle on={on} onChange={(v) => setStatus(v ? "on" : "off")} label={on ? "Turn off" : "Turn on"} />
      </div>
    </div>
  );
  const notes = (
    <>
      {waiting ? (
        <div className="au-note">
          <Icon name="clock" />
          <span>
            <b>Starts when <bdi>{waiting.title}</bdi> is published.</b>
          </span>
          <Button onClick={() => navigate(`/rooms/${waiting.webinar_id}`)}>Open the room</Button>
        </div>
      ) : null}
      {status === "off" ? (
        <div className="au-note">
          <Icon name="pause" />
          <span>
            <b>Paused.</b>
            {inNow ? ` ${people(inNow)} wait where they are.` : ""}
          </span>
          <Button onClick={() => setStatus("on")}>Turn it back on</Button>
        </div>
      ) : null}
    </>
  );
  const steps = <StepsLine editor={editor} who={who} whenSub={whenSub} open={side ? target : null} onOpen={setTarget} />;
  const how = <HowCard d={d} />;
  const now = <NowCard who={who} editor={editor} />;
  const runs = <RunsCard d={d} onRoom={(w, v) => changed(A.setRoom(d, w, v))} onAdd={() => setAdding(true)} />;
  const attention = <AttentionCard d={d} busy={busy} onSettle={settle} />;
  const summaries = failed ? (
    <>
      {attention}
      {how}
      {now}
      {runs}
    </>
  ) : (
    <>
      {how}
      {now}
      {attention}
      {runs}
    </>
  );

  const sheets = (
    <>
      <ActionsSheet
        open={menu}
        onClose={() => setMenu(false)}
        title={d.name}
        actions={[
          { icon: "pencil", label: "Rename", onSelect: () => setRenaming(true) },
          { icon: "trash", label: "Delete", danger: true, onSelect: () => setDeleting(true) },
        ]}
      />
      {renaming ? <RenameSheet open onClose={() => setRenaming(false)} name={d.name} onSave={(name) => changed(A.rename(d, editor, name))} /> : null}
      <DeleteSheet
        open={deleting}
        onClose={() => setDeleting(false)}
        name={d.name}
        onDelete={() => {
          setDeleting(false);
          changed(A.remove(d));
        }}
      />
      <AttentionSheet open={attn} onClose={() => setAttn(false)} d={d} onSettle={settle} />
      <ActionsSheet
        open={adding}
        onClose={() => setAdding(false)}
        title="Add a room"
        actions={d.webinars
          .filter((w) => !w.attached)
          .map((w) => ({ icon: "play" as const, label: w.title, onSelect: () => changed(A.setRoom(d, w.webinar_id, true)) }))}
      />
      {!side && target ? <StepSheet key={target} editor={editor} target={target} save={save} close={() => setTarget(null)} /> : null}
    </>
  );

  if (side) {
    return (
      <>
        <Pane kind="main" showing={id} label="The automation that is open">
          <Page layout="side" className="au">
            {hero}
            <div className="au-top">{statusSwitch}</div>
            {notes}
            {steps}
          </Page>
        </Pane>
        <Pane kind="detail" showing={`${id}:${target ?? ""}`} label={target ? "The open step" : "How it is going"}>
          <Page layout="side" className="au">
            {target ? <StepPane key={target} editor={editor} target={target} save={save} close={() => setTarget(null)} /> : <div className="au-col">{summaries}</div>}
          </Page>
        </Pane>
        {sheets}
      </>
    );
  }

  const bar = draft ? (
    <DockedBar>
      <Button variant="p" full onClick={() => void finish()}>
        Finish it
      </Button>
    </DockedBar>
  ) : (
    <DockedBar>
      <span className="k-bar-t">
        <b>
          {on ? "On" : "Off"}
          {inNow ? `, ${people(inNow)} in it` : ""}
        </b>
      </span>
      <Toggle on={on} onChange={(v) => setStatus(v ? "on" : "off")} label={on ? "Turn off" : "Turn on"} />
    </DockedBar>
  );

  return (
    <Page layout={layout} narrow className="au">
      {nav(menuButton)}
      {hero}
      {phone ? (
        <>
          {notes}
          {failed ? (
            <GroupedList className="au-fault">
              <ListRow icon="info" tone="brass" title={failed.words} sub={failed.reason} onClick={() => setAttn(true)} />
            </GroupedList>
          ) : null}
          {steps}
          {how}
          {now}
          {runs}
          {bar}
        </>
      ) : (
        <>
          <div className="au-top">{statusSwitch}</div>
          {notes}
          <div className="k-two au-two">
            <div>{steps}</div>
            <div className="au-col">{summaries}</div>
          </div>
        </>
      )}
      {sheets}
    </Page>
  );
}
